/**
 * The one "main reason" the Results hero and the board pack both say (U2-5): the largest bar of the waterfall "What moved it" draws, named the way the bar is named. The bridge's own largest step
 * (`reasonSentence`) is a different basis (it folds cash and capital together) and is no longer shown beside the waterfall.
 */
import type { ContentCatalog, DraftView, RoundReport } from "@bsim/contracts";
import { formatUsdPerShare } from "../format";
import { lowerFirst } from "../format/labels";
import { decompose, type Bar } from "./decomposition";
import { largestStep, reasonLabel } from "./yearStory";

/** Under half a cent a figure reads as no move. */
const LEVEL_USD = 0.005;

type Leg = Pick<Bar, "kind" | "label" | "delta" | "full">;

/** The largest leg, either sign, of the decomposition "What moved it" draws for the score: the hero names the same bar with the same label. Null when nothing moved. */
export function biggestLeg(report: RoundReport, catalog: ContentCatalog, draft: DraftView | null = null): Leg | null {
  const drawn = decompose(report, catalog, "valueCreated", "twins", draft)?.bars;
  // A report with no attribution draws no waterfall; its bridge is then the only decomposition there is.
  const step = drawn === undefined ? largestStep(report.scorecard.valueBridge) : null;
  const bars: readonly Leg[] = drawn ?? (step === null ? [] : [{ kind: "market", label: reasonLabel(step, catalog.glossary), delta: step.usdPerShare }]);
  // The reason names a cause that pulled the same way as the score's gap (a shortfall is explained by its largest negative part, never by a gain); with none the right way, the largest of either sign.
  const gap = report.score.valueCreatedUsdPerShare;
  const sameWay = Math.abs(gap) < LEVEL_USD ? bars : bars.filter((b) => b.delta * gap > 0);
  const top = (sameWay.length === 0 ? bars : sameWay).reduce<Leg | null>((best, b) => (best === null || Math.abs(b.delta) > Math.abs(best.delta) ? b : best), null);
  return top === null || Math.abs(top.delta) < LEVEL_USD ? null : top;
}


/** "Biggest single reason: your card answers (+$0.46)": the clause both screens print, from the bar the waterfall draws. */
export const mainReasonClause = (leg: Leg): string =>
  `Biggest single reason: ${leg.kind === "decision" ? leg.label : lowerFirst(leg.kind === "preview" ? leg.label : leg.full ?? leg.label)} (${formatUsdPerShare(leg.delta, { signed: true })})`;

/** The sentence for the board pack; null when nothing moved. */
export function mainReasonSentence(report: RoundReport, catalog: ContentCatalog, draft: DraftView | null = null): string | null {
  const leg = biggestLeg(report, catalog, draft);
  return leg === null ? null : `${mainReasonClause(leg)}.`;
}
